/*!
 * SkyPlus Document Indexing Service
 * Location: app/src/app/components/skyplus/indexing/indexing.service.ts
 * 
 * This service handles:
 * - File upload with custom metadata
 * - Folder creation from field values (MAIN folder)
 * - Subfolder creation from field values (NEW)
 * - Metadata updates on existing documents
 */

import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { AuthenticationService, AppConfigService } from '@alfresco/adf-core';
import { 
  DocumentField,
  getDocumentTypeById, 
  buildAlfrescoProperties,
  getSubfolderName
} from './document-types.config';

export interface UploadOptions {
  /** The file to upload */
  file: File;
  
  /** Document type ID from config */
  documentTypeId?: string;
  
  /** Target folder ID */
  targetFolderId: string;
  
  /** Form values for metadata */
  metadata?: Record<string, any>;
  
  /** Field to use for creating MAIN folder (property name) */
  createFolderFromField?: string;
  
  /** Field to use for creating SUBFOLDER within main folder (property name) - NEW */
  createSubfolderFromField?: string;
  
  /** The field definition for subfolder (needed to get label for select fields) - NEW */
  subfolderFieldDef?: DocumentField;
  
  /**
   * Strike E — ordered sub-folder names to create under the resolved parent
   * (config-driven filing). When present, these supersede the legacy
   * createFolderFromField / createSubfolderFromField two-level logic. Empty or
   * absent → the original behaviour is used, so static types are unchanged.
   */
  folderSegments?: string[];
  
  /** Whether to auto-rename if file exists */
  autoRename?: boolean;
}

export interface UploadResult {
  success: boolean;
  nodeId?: string;
  nodeName?: string;
  folderId?: string;
  folderPath?: string;
  error?: string;
}

export interface FolderInfo {
  id: string;
  name: string;
  path: string;
}

@Injectable({
  providedIn: 'root'
})
export class IndexingService {
  
  private ecmHost = '';
  
  constructor(
    private http: HttpClient,
    private authService: AuthenticationService,
    private appConfig: AppConfigService
  ) {
    // Get ecmHost from app.config.json (e.g., "http://alfresco.local:8086")
    this.ecmHost = this.appConfig.get<string>('ecmHost', '') || '';
    if (this.ecmHost.endsWith('/')) {
      this.ecmHost = this.ecmHost.slice(0, -1);
    }
  }
  
  /**
   * Build full Alfresco API URL using ecmHost from app.config.json.
   * Routes through nginx which validates the Bearer token via auth_request.
   */
  private getApiUrl(path: string): string {
    return `${this.ecmHost}${path}`;
  }
  
  /**
   * Get HTTP headers with Bearer token authentication (Keycloak).
   */
  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    let headers = new HttpHeaders();
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  }
  
  /**
   * Get current username
   */
  getCurrentUsername(): string {
    return this.authService.getEcmUsername() || 'unknown';
  }

  private readonly SYSTEM_FOLDERS = [
    'Data Dictionary', 'Sites', 'Imap Attachments', 'IMAP Home', 'Guest Home',
    'User Homes', 'Impress Templates', 'Scripts', 'Web Scripts',
    'Web Scripts Extensions', 'Models', 'Messages', 'Transfers',
    'Rendering Actions Space', 'Replication Actions Space', 'Email Templates',
    'RSS Templates', 'Activity Feed Email Templates', 'Workflow Notification',
    'Invite Email Templates', 'Node Templates', 'Space Templates', 'Saved Searches',
    'Scheduled Actions', 'Smart Folder Downloads', 'Smart Folder Templates', 'Solr Facets Space'
  ];
  
  /**
   * Upload a file with optional indexing metadata
   * Supports creating MAIN folder and optional SUBFOLDER within it
   */
  async uploadWithMetadata(options: UploadOptions): Promise<UploadResult> {
    const { 
      file, 
      documentTypeId, 
      targetFolderId, 
      metadata, 
      createFolderFromField,
      createSubfolderFromField,
      subfolderFieldDef,
      autoRename = true 
    } = options;
    
    console.log('uploadWithMetadata called with:', {
      fileName: file.name,
      targetFolderId,
      documentTypeId,
      createFolderFromField,
      createFolderValue: createFolderFromField && metadata ? metadata[createFolderFromField] : null,
      createSubfolderFromField,
      createSubfolderValue: createSubfolderFromField && metadata ? metadata[createSubfolderFromField] : null
    });
    
    try {
      let finalFolderId = targetFolderId;
      let folderPath = '';
      
      // Strike E — config-driven ordered folder segments supersede the legacy
      // main + subfolder fields. When no segments are supplied (static types),
      // we fall through to the original two-level logic, so existing behaviour
      // is byte-for-byte unchanged.
      if (options.folderSegments && options.folderSegments.length > 0) {
        for (const segment of options.folderSegments) {
          const segName = String(segment).trim();
          if (segName) {
            const segResult = await this.getOrCreateFolder(finalFolderId, segName);
            finalFolderId = segResult.id;
            folderPath = segResult.path;
            console.log('Folder segment created/found:', segResult);
          }
        }
      } else {
        // STEP 1: Create MAIN folder if specified
        if (createFolderFromField && metadata && metadata[createFolderFromField]) {
          const mainFolderName = String(metadata[createFolderFromField]).trim();
          console.log('Creating MAIN folder:', mainFolderName, 'in parent:', targetFolderId);
          
          if (mainFolderName) {
            const mainFolderResult = await this.getOrCreateFolder(targetFolderId, mainFolderName);
            finalFolderId = mainFolderResult.id;
            folderPath = mainFolderResult.path;
            console.log('MAIN folder created/found:', mainFolderResult);
          }
        }
        
        // STEP 2: Create SUBFOLDER within main folder if specified (NEW)
        if (createSubfolderFromField && metadata && metadata[createSubfolderFromField]) {
          let subfolderName: string;
          
          // Get subfolder name - use label for select fields if configured
          if (subfolderFieldDef) {
            subfolderName = getSubfolderName(subfolderFieldDef, metadata[createSubfolderFromField]);
          } else {
            subfolderName = String(metadata[createSubfolderFromField]).trim();
          }
          
          console.log('Creating SUBFOLDER:', subfolderName, 'in parent:', finalFolderId);
          
          if (subfolderName) {
            const subfolderResult = await this.getOrCreateFolder(finalFolderId, subfolderName);
            finalFolderId = subfolderResult.id;
            folderPath = subfolderResult.path;
            console.log('SUBFOLDER created/found:', subfolderResult);
          }
        }
      }
      
      console.log('Final upload folder ID:', finalFolderId);
      
      // Build the upload request
      const formData = new FormData();
      formData.append('filedata', file, file.name);
      formData.append('name', file.name);
      formData.append('autoRename', String(autoRename));
      
      // If document type is selected, use custom node type
      if (documentTypeId) {
        const docType = getDocumentTypeById(documentTypeId);
        if (docType) {
          formData.append('nodeType', docType.nodeType);
        }
      } else {
        formData.append('nodeType', 'cm:content');
      }
      
      // Build URL using ecmHost (goes through nginx with JWT validation)
      const uploadUrl = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${finalFolderId}/children`
      );
      
      // Upload the file with Bearer token auth
      const uploadResponse: any = await this.http.post(uploadUrl, formData, {
        headers: this.getAuthHeaders()
      }).toPromise();
      const nodeId = uploadResponse?.entry?.id;
      
      if (!nodeId) {
        throw new Error('Upload succeeded but no node ID returned');
      }
      
      // If we have metadata and a document type, update the node properties
      if (documentTypeId && metadata && Object.keys(metadata).length > 0) {
        await this.updateNodeMetadata(nodeId, documentTypeId, metadata);
      }
      
      return {
        success: true,
        nodeId: nodeId,
        nodeName: uploadResponse?.entry?.name,
        folderId: finalFolderId,
        folderPath: folderPath
      };
      
    } catch (error: any) {
      console.error('Upload error:', error);
      return {
        success: false,
        error: error.message || error.statusText || 'Unknown error'
      };
    }
  }
  
  /**
   * Update node metadata/properties
   */
  async updateNodeMetadata(
    nodeId: string, 
    documentTypeId: string, 
    metadata: Record<string, any>,
    extraAspects: string[] = [],
    extraProps: Record<string, any> = {}
  ): Promise<boolean> {
    try {
      const docType = getDocumentTypeById(documentTypeId);
      if (!docType) {
        console.warn('Document type not found:', documentTypeId);
        return false;
      }
      
      // Build properties object
      const properties = { ...buildAlfrescoProperties(documentTypeId, metadata), ...extraProps };
      
      // Strike D — aspects to stamp so indexed properties are searchable.
      // Runtime (registry) types carry aspectNames[]; static types fall back
      // to their single aspectName. Without this, properties defined on an
      // aspect (e.g. eacc:staffFileIndexed) are silently dropped by Alfresco
      // and the document is never findable by those fields.
      const aspectNames: string[] =
        (docType.aspectNames && docType.aspectNames.length)
          ? [...docType.aspectNames]
          : (docType.aspectName ? [docType.aspectName] : []);
      // Merge any caller-supplied extra aspects (e.g. eacc:folioed for folio).
      for (const a of extraAspects) {
        if (a && !aspectNames.includes(a)) { aspectNames.push(a); }
      }
      
      // Build update URL using ecmHost
      const updateUrl = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}`
      );
      
      // Update the node with Bearer token auth. Include aspectNames when the
      // type declares any — Alfresco adds them (system/mandatory aspects are
      // retained) before applying the properties they define.
      const updateBody: any = aspectNames.length
        ? { aspectNames, properties }
        : { properties };
      await this.http.put(updateUrl, updateBody, {
        headers: this.getAuthHeaders()
      }).toPromise();
      
      return true;
    } catch (error) {
      console.error('Error updating metadata:', error);
      return false;
    }
  }
  
  /**
   * Get or create a folder by name within a parent folder
   */
  /**
   * Strike E — resolve a repository path to a folder node id, creating any
   * missing folders along the way. Walks from {@code -root-} (Company Home), so
   * it transparently handles both plain paths
   * ({@code /EACC-ECM/APPEALS}) and SITE paths
   * ({@code /Sites/<siteId>/documentLibrary/<folder>}) — the site and its
   * documentLibrary must already exist (they're created with the site), but any
   * leaf folders below are auto-created.
   */
  async resolveOrCreatePath(path: string): Promise<string> {
    const segments = (path || '')
      .split('/')
      .map(s => s.trim())
      .filter(s => s.length > 0);
    let parentId = '-root-';
    for (const segment of segments) {
      const folder = await this.getOrCreateFolder(parentId, segment);
      parentId = folder.id;
    }
    return parentId;
  }

  /**
   * Round 7 — walk an ordered list of folder segments under a parent,
   * creating each level idempotently. The segments come from the backend
   * folder-rule engine (resolve-folders), so every capture entry point
   * produces the identical structure. Returns the final folder id.
   */
  async createSegmentPath(parentFolderId: string, segments: string[]): Promise<string> {
    let parentId = parentFolderId;
    for (const seg of (segments || [])) {
      const clean = (seg || '').trim();
      if (!clean) { continue; }
      const folder = await this.getOrCreateFolder(parentId, clean);
      parentId = folder.id;
    }
    return parentId;
  }

  /**
   * LOOKUP-FIRST get-or-create (matches the backend's
   * AlfrescoFilingSyncService.getOrCreateFolder order).
   *
   * The old create-first strategy POSTed unconditionally and only fell back
   * to a lookup on 409. At levels the user cannot write — e.g. Company Home
   * when resolving a base path like /EACC Documents/... — the POST returns
   * 403 (NOT 409), the fallback never ran, and resolution failed even
   * though the folder existed and the user could read it. A site manager's
   * rights apply only INSIDE the site, so root-level creates always 403 for
   * non-admins. Lookup-first makes traversing existing paths a read-only
   * operation; creation happens only where a level is genuinely missing,
   * which then correctly requires write permission at that level.
   */
  async getOrCreateFolder(parentFolderId: string, folderName: string): Promise<FolderInfo> {
    const found = await this.findChildFolder(parentFolderId, folderName);
    if (found) { return found; }

    try {
      const createUrl = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${parentFolderId}/children`
      );
      const createResponse: any = await this.http.post(createUrl, {
        name: folderName,
        nodeType: 'cm:folder'
      }, {
        headers: this.getAuthHeaders()
      }).toPromise();
      return {
        id: createResponse.entry.id,
        name: createResponse.entry.name,
        path: createResponse.entry.path?.name || ''
      };
    } catch (error: any) {
      // Concurrent create by another user — re-resolve and use theirs.
      if (error.status === 409) {
        const raced = await this.findChildFolder(parentFolderId, folderName);
        if (raced) { return raced; }
      }
      if (error.status === 403) {
        console.error(`No permission to create folder '${folderName}' at this level ` +
          `(and it does not exist yet). An administrator must pre-create it or grant rights.`);
      } else {
        console.error('Error creating folder:', error);
      }
      throw error;
    }
  }

  /** Find a direct child folder by exact name; null when absent. */
  private async findChildFolder(parentFolderId: string, folderName: string): Promise<FolderInfo | null> {
    try {
      const listUrl = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${parentFolderId}` +
        `/children?where=(isFolder=true)&maxItems=1000`
      );
      const listResponse: any = await this.http.get(listUrl, {
        headers: this.getAuthHeaders()
      }).toPromise();
      const existing = listResponse?.list?.entries?.find((entry: any) =>
        entry.entry.isFolder && entry.entry.name === folderName
      );
      return existing ? {
        id: existing.entry.id,
        name: existing.entry.name,
        path: existing.entry.path?.name || ''
      } : null;
    } catch (listError) {
      console.error('Error listing folders:', listError);
      return null;
    }
  }
  
  /**
   * Get folder contents for browsing
   */
  async getFolderContents(folderId: string): Promise<any[]> {
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${folderId}/children`
      );
      const params = new HttpParams()
        .set('where', '(isFolder=true)')
        .set('orderBy', 'name ASC')
        .set('include', 'path')
        .set('maxItems', '100');

      const result: any = await this.http.get(url, {
        headers: this.getAuthHeaders(),
        params
      }).toPromise();

      return (result?.list?.entries || [])
        .filter((e: any) => !this.SYSTEM_FOLDERS.includes(e.entry.name))
        .map((e: any) => ({
          id: e.entry.id,
          name: e.entry.name,
          path: e.entry.path?.name || ''
        }));
    } catch (error) {
      console.error('Error getting folder contents:', error);
      return [];
    }
  }
  
  /**
   * Get node details including properties
   */
  /**
   * Phase 5 — move a node to a new parent folder (used by re-file/move).
   * POST /nodes/{nodeId}/move with { targetParentId }.
   */
  async moveNode(nodeId: string, targetParentId: string): Promise<boolean> {
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/move`
      );
      await this.http.post(url, { targetParentId }, {
        headers: this.getAuthHeaders()
      }).toPromise();
      return true;
    } catch (error) {
      console.error('Error moving node:', error);
      return false;
    }
  }

  async getNodeDetails(nodeId: string): Promise<any> {
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}?include=properties,path,aspectNames`
      );
      const result: any = await this.http.get(url, {
        headers: this.getAuthHeaders()
      }).toPromise();
      return result?.entry || null;
    } catch (error) {
      console.error('Error getting node details:', error);
      return null;
    }
  }
  
  /**
   * Get the path to a node
   */
  async getNodePath(nodeId: string): Promise<string> {
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}?include=path`
      );
      const result: any = await this.http.get(url, {
        headers: this.getAuthHeaders()
      }).toPromise();
      return result?.entry?.path?.name || '';
    } catch (error) {
      return '';
    }
  }
  
  /**
   * Check if user has write permission to a folder
   */
  async hasWritePermission(_folderId: string): Promise<boolean> {
    // For now, assume write permission if we can read
    return true;
  }
  
  /**
   * Get a preview URL for a file.
   */
  getPreviewUrl(nodeId: string): string {
    return this.getApiUrl(
      `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content`
    );
  }
  
  /**
   * Get a thumbnail URL for a file.
   */
  getThumbnailUrl(nodeId: string, thumbnailId: string = 'doclib'): string {
    return this.getApiUrl(
      `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/renditions/${thumbnailId}/content`
    );
  }
  
  /**
   * Fetch (creating if necessary) the Alfresco 'pdf' rendition of a node as a Blob — used to
   * preview Office documents (.docx/.xlsx/.pptx…) inline. Goes through ecmHost with the Bearer
   * token (the bearer-validating Alfresco endpoint), exactly like every other call here, so it
   * works where a direct backend→ACS call would not. Returns null if the rendition can't be made.
   */
  async getPdfRenditionAsBlob(nodeId: string): Promise<Blob | null> {
    const contentUrl = this.getApiUrl(
      `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/renditions/pdf/content`
    );
    // 1) Try directly — the pdf rendition usually already exists.
    try {
      const blob = await this.http.get(contentUrl, {
        headers: this.getAuthHeaders(), responseType: 'blob'
      }).toPromise();
      if (blob && blob.size > 0) { return blob; }
    } catch { /* not ready yet — create + poll below */ }

    // 2) Ask Alfresco to create it (409 = already requested), then poll until CREATED.
    const base = this.getApiUrl(
      `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/renditions`
    );
    try {
      await this.http.post(base, { id: 'pdf' }, { headers: this.getAuthHeaders() }).toPromise();
    } catch { /* 409 already requested — ignore */ }

    for (let i = 0; i < 20; i++) {                 // ~30s ceiling
      await new Promise(res => setTimeout(res, 1500));
      try {
        const meta: any = await this.http.get(`${base}/pdf`, {
          headers: this.getAuthHeaders()
        }).toPromise();
        if (meta?.entry?.status === 'CREATED') {
          const blob = await this.http.get(contentUrl, {
            headers: this.getAuthHeaders(), responseType: 'blob'
          }).toPromise();
          if (blob && blob.size > 0) { return blob; }
        }
      } catch { /* keep polling */ }
    }
    return null;
  }

  /**
   * Fetch content as blob (for preview/download with Bearer auth).
   */
  async getContentAsBlob(nodeId: string): Promise<Blob | null> {
    try {
      const url = this.getApiUrl(
        `/alfresco/api/-default-/public/alfresco/versions/1/nodes/${nodeId}/content?attachment=false`
      );
      return await this.http.get(url, {
        headers: this.getAuthHeaders(),
        responseType: 'blob'
      }).toPromise() || null;
    } catch (error) {
      console.error('Error fetching content blob:', error);
      return null;
    }
  }
}